/**
 * Personalised "You may also like" scorer.
 *
 * Ranks every design in the merged catalog by similarity to the design the
 * shopper is currently viewing, with a light boost from what they've already
 * saved to their wishlist. Deterministic, no LLM call — runs instantly
 * client-side so the section is on-screen the moment the detail page paints.
 */

const _weightBucket = (grams) => {
  if (!grams) return "unknown";
  if (grams < 5) return "featherlight";
  if (grams < 12) return "light";
  if (grams < 25) return "medium";
  if (grams < 60) return "heavy";
  return "statement";
};

// Score a candidate against a single reference design (current OR wishlist).
const scoreAgainst = (candidate, ref, weight = 1) => {
  if (!candidate || !ref) return 0;
  let s = 0;
  if (candidate.subCategory?.slug === ref.subCategory?.slug) s += 5;
  else if (candidate.category?.slug === ref.category?.slug) s += 3;
  if (candidate.gender && candidate.gender === ref.gender) s += 2;
  if (candidate.purity && candidate.purity === ref.purity) s += 2;
  if (_weightBucket(candidate.weight) === _weightBucket(ref.weight)) s += 1;
  return s * weight;
};

export function relatedDesigns(current, wishlistItems, allDesigns, limit = 3) {
  if (!current || !allDesigns?.length) return [];
  const seenIds = new Set([current.id, ...(wishlistItems || []).map((w) => w.id)]);
  const scored = [];

  for (const d of allDesigns) {
    if (seenIds.has(d.id)) continue;
    // Base similarity to the design being viewed.
    let score = scoreAgainst(d, current, 1);
    // Wishlist boost — each saved item nudges its close cousins up a bit.
    // Capped at 3 items so a huge wishlist doesn't dominate the current context.
    if (wishlistItems?.length) {
      for (const w of wishlistItems.slice(0, 3)) {
        score += scoreAgainst(d, w, 0.4);
      }
    }
    if (score > 0) scored.push({ d, score });
  }

  scored.sort((a, b) => b.score - a.score);
  // If we can't fill the row with scored matches, top up with same-category
  // fallbacks so the section never renders empty.
  const picks = scored.slice(0, limit).map((s) => s.d);
  if (picks.length < limit) {
    for (const d of allDesigns) {
      if (picks.length >= limit) break;
      if (seenIds.has(d.id) || picks.some((p) => p.id === d.id)) continue;
      if (d.category?.slug === current.category?.slug) picks.push(d);
    }
  }
  return picks.slice(0, limit);
}
